import { WIRE_OFFSET_DATETIME_PATTERN } from "./wire-patterns";
import { z } from "zod";
import { communityActivationSchema, communityLevelSchema, communityNextLevelSchema, communityPodiumPlaceSchema } from "./community-holders-v1";
import { communityXProfileImageSchema, referralCodeSchema } from "./community-v1";
import type { CommunityPublicProfileResponse } from "./community-progress-v1";

/**
 * Genesis campaign surfaces: public identity, Aura standing, badge rarity and allowlist status.
 *
 * Aura is a rarity-weighted score computed in periodic snapshots. Totals, ranks and how many
 * members hold each badge are public; the per-badge value and the formula are not, so no schema
 * here carries them.
 */

const countSchema = z.number().refine(Number.isInteger).meta({ type: "integer" }).nonnegative();
const dateTimeSchema = z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN);
const achievementIdSchema = z.string().regex(/^[a-z][a-z0-9]*(\.[a-z0-9_]+)+$/);
const addressSchema = z.string().regex(/^0x[0-9a-f]{40}$/);

/**
 * Minimum proven X-account age for joining Genesis (linking X completes signup) and for every
 * whitelist seat.
 */
export const GENESIS_MIN_X_ACCOUNT_AGE_DAYS = 365;
/** Minimum X followers for joining Genesis. */
export const GENESIS_MIN_X_FOLLOWERS = 25;
/** Minimum X posts (X's `tweet_count`) for joining Genesis. */
export const GENESIS_MIN_X_POSTS = 50;

/**
 * Why an X account does not meet the Genesis member criteria. Unknown facts fail: X did not report
 * the creation date or a count, or the account has no picture (or X's default silhouette).
 */
export const genesisMemberCriteriaFailureSchema = z.enum([
  "x_missing",
  "x_too_new",
  "x_few_followers",
  "x_few_posts",
  "x_default_picture",
]);
export type GenesisMemberCriteriaFailure = z.infer<typeof genesisMemberCriteriaFailureSchema>;

/** The only member criteria an audited, X-id-bound admin approval may waive. */
export const APPROVABLE_X_CRITERIA = ["x_few_followers", "x_few_posts"] as const satisfies readonly GenesisMemberCriteriaFailure[];

/**
 * Invitations stop attributing new members once at least `minAttempts` invitees tried to join
 * (linked X or were refused) and at least `failureShare` of them fail the member criteria or are
 * disqualified.
 */
export const GENESIS_INVITE_FREEZE = { minAttempts: 10, failureShare: 0.5 } as const;

/** Silent wallet-history review: count distinct members, never wallets or cross-chain matches. */
export const GENESIS_WALLET_CLUSTER = { minNonce: 5, minMembers: 10 } as const;
/** Public mainnets scanned for Genesis wallet-history review; native/test chains are not evidence. */
export const GENESIS_WALLET_HISTORY_CHAIN_IDS = [1, 8453, 56, 42161] as const;

/**
 * Whether a member's invitation link brings in invitees: `active`, or why not (admin view). Only an
 * active inviter is recorded as the invitee's inviter; everyone else can still join without one.
 */
export const genesisInviteStatusSchema = z.enum(["active", "x_missing", "below_criteria", "frozen", "disqualified"]);
export type GenesisInviteStatus = z.infer<typeof genesisInviteStatusSchema>;

/**
 * The invite status a member sees about themselves. It never says why a link is inactive, so it
 * cannot be used to probe the member criteria or the freeze.
 */
export const genesisMemberInviteStatusSchema = z.enum(["active", "x_missing", "inactive"]);
export type GenesisMemberInviteStatus = z.infer<typeof genesisMemberInviteStatusSchema>;

/**
 * Sign-ups through one invitation code within a rolling `windowMinutes`: beyond `flagAbove` each new
 * member (and the code's owner) is flagged for review without telling anyone; beyond `rejectAbove`
 * the sign-up is refused with a generic error.
 */
export const GENESIS_INVITE_BURST = { windowMinutes: 60, flagAbove: 50, rejectAbove: 100 } as const;

/** Minimum Discord-account age for badge credit and requireDiscord eligibility. */
export const GENESIS_MIN_DISCORD_ACCOUNT_AGE_DAYS = 365;

/** A single-word Squeek or Ham name; never prefixed with "@" and never linked to X. */
export const communityAliasSchema = z.string().regex(/^[A-Za-z]{2,32}$/);

/**
 * Whitelist tiers (D14): a tier sets how many NFTs a whitelisted wallet may buy, not its odds.
 * Direct game winners are Gold. Members who earned a badge beyond signing up (partner holder badges
 * don't count) are active and take Gold, Silver or Bronze by their place among active members;
 * everyone else is Member.
 */
export const genesisTierSchema = z.enum(["gold", "silver", "bronze", "member"]);
export type GenesisTier = z.infer<typeof genesisTierSchema>;

/** Tiers from best to worst. */
export const GENESIS_TIERS: readonly GenesisTier[] = ["gold", "silver", "bronze", "member"];

/** Whitelist buying limit per tier; each follows a merge level (10 = a Level 4 plus a Level 2). */
export const GENESIS_TIER_ENTRY_LIMITS: Readonly<Record<GenesisTier, number>> = {
  gold: 10,
  silver: 4,
  bronze: 2,
  member: 1,
};

/**
 * Gold and Silver as whole-number percentages of active members, best Aura first; Bronze takes the
 * remaining active members. Shares, not counts, so tiers stay as scarce as the campaign grows.
 */
export const genesisTierSharesSchema = z.object({
  gold: z.number().int().min(1).max(100),
  silver: z.number().int().min(0).max(99),
}).strict().refine((shares) => shares.gold + shares.silver <= 100, { message: "gold and silver shares exceed 100%" });
export type GenesisTierShares = z.infer<typeof genesisTierSharesSchema>;

export const GENESIS_DEFAULT_TIER_SHARES: GenesisTierShares = { gold: 10, silver: 30 };

/**
 * The last active position Gold, Silver or Bronze reaches among `active` members (rounded up, so a
 * tier with any share holds at least one place once there is an active member); `null` for Member.
 */
export function genesisTierCutoff(tier: GenesisTier, active: number, shares: GenesisTierShares): number | null {
  if (tier === "gold") return Math.ceil((active * shares.gold) / 100);
  if (tier === "silver") return Math.ceil((active * (shares.gold + shares.silver)) / 100);
  if (tier === "bronze") return active;
  return null;
}

/**
 * A member's tier: `null` past the last whitelist seat (`rank` among all members); Member without
 * an `activePosition` (signup badges only); otherwise Gold, Silver or Bronze by 1-based place among
 * the `active` members. Game winners are Gold before this applies.
 */
export function genesisTierAt(
  standing: { readonly rank: number; readonly activePosition: number | null },
  active: number,
  shares: GenesisTierShares,
  seats: number,
): GenesisTier | null {
  const { rank, activePosition } = standing;
  if (!Number.isInteger(rank) || rank < 1 || rank > seats) return null;
  if (activePosition === null) return "member";
  if (activePosition <= genesisTierCutoff("gold", active, shares)!) return "gold";
  if (activePosition <= genesisTierCutoff("silver", active, shares)!) return "silver";
  return "bronze";
}

/**
 * Member colours: the member card's LED ramps (warm, blue, pink, green, then their partners coral,
 * cyan, violet, lime), in this order. Grow only by doubling: `hash % 2n` is `hash % n` or that plus
 * `n`, so every member keeps their colour or moves to its partner.
 */
export const MEMBER_COLOR_COUNT = 8;

/** A stable index in `0..count - 1` from a seed (FNV-1a over the lowercase seed). */
export function seededIndex(seed: string, count: number): number {
  let hash = 2166136261;
  for (const char of seed.toLowerCase()) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return (hash >>> 0) % count;
}

/**
 * A member's colour, 0 to `MEMBER_COLOR_COUNT - 1`, from their profile code. The web app picks the
 * card's LED ramp with it, and Community uses it to colour invitees without naming them, so both
 * always agree.
 */
export function memberColorIndex(seed: string): number {
  return seededIndex(seed, MEMBER_COLOR_COUNT);
}

/** Bump when the social card's layout, fonts or LED drawing changes, so previews fetch a new image. */
export const MEMBER_CARD_DESIGN_VERSION = 1;

/** Everything about a member the social card shows: its render cache key and its preview URL's version. */
export interface MemberCardFacts {
  readonly code: string;
  /** The X username without "@", or the alias. */
  readonly name: string;
  readonly rank: number | null;
  readonly aura: number;
  readonly tier: GenesisTier | null;
  /** Badges earned. */
  readonly badges: number;
  /** The X picture the portrait is drawn from; `null` draws the hamster. */
  readonly pictureUrl: string | null;
}

/** The card's facts from a public profile; `null` when the profile has no public card. */
export function memberCardFacts(profile: CommunityPublicProfileResponse): MemberCardFacts | null {
  const identity = profile.identity;
  if (!profile.valid || identity === null) return null;
  return {
    code: profile.code,
    name: identity.kind === "x" ? identity.username : identity.alias,
    rank: profile.standing.rank,
    aura: profile.standing.aura,
    tier: profile.standing.tier,
    badges: profile.achievements.length,
    pictureUrl: identity.kind === "x" ? identity.profileImageUrl : null,
  };
}

/**
 * How a member appears on public pages. `x` only while the member shows their X identity (the
 * default), the gateway's latest read is public and under 24 hours old; otherwise their alias.
 */
export const communityPublicIdentitySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("x"),
    username: z.string().min(1).max(64),
    name: z.string().min(1).max(128),
    profileImageUrl: communityXProfileImageSchema,
  }).strict(),
  z.object({ kind: z.literal("alias"), alias: communityAliasSchema }).strict(),
]);

/**
 * A server-resolved invitation. `valid` is the only authority on whether the code will be applied at
 * signup: an unknown code and a known code whose owner is disqualified are both `false`. `referrer`
 * is the inviter's public identity (X or alias) whenever `valid`.
 */
export const communityReferralLookupResponseSchema = z.object({
  code: referralCodeSchema,
  valid: z.boolean(),
  referrer: communityPublicIdentitySchema.nullable(),
}).strict();

/** Rank decides the allowlist; Aura is shown beside it, rounded to a whole number. */
export const communityAuraStandingSchema = z.object({
  /** `null` while the member is not eligible or no snapshot includes them yet. */
  rank: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive().nullable(),
  aura: countSchema,
  /**
   * The whitelist tier the member's position holds now; `null` with no Aura or past the last seat.
   * Live: game winners added by an operator can still move members down until the freeze.
   */
  tier: genesisTierSchema.nullable(),
  /** The next tier up and the Aura that would reach it now; `null` at Gold or before ranking. */
  next: z.object({ tier: genesisTierSchema, auraNeeded: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive() }).strict().nullable(),
  /**
   * Places climbed since the member's first rank of the current UTC day (negative: places lost);
   * `null` while unranked or before today's first ranking.
   */
  movement: z.number().refine(Number.isInteger).meta({ type: "integer" }).nullable(),
}).strict();

/** "Held by 25% of members": holders among eligible members, in basis points of `members`. */
export const communityBadgeRaritySchema = z.object({
  holders: countSchema,
  heldByBps: z.number().int().min(0).max(10_000),
}).strict();

export const communityAuraSnapshotSchema = z.object({
  /** Eligible members the snapshot counted: signup complete, not disqualified. */
  members: countSchema,
  computedAt: dateTimeSchema,
}).strict();

export const communityAuraLeaderboardResponseSchema = z.object({
  entries: z.array(z.object({
    rank: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
    /** The member's public profile code (`/genesis/<code>`). */
    code: referralCodeSchema,
    identity: communityPublicIdentitySchema,
    aura: countSchema,
    badges: countSchema,
    tier: genesisTierSchema.nullable(),
  }).strict()).max(100),
  /** `null` until the first snapshot has been computed. */
  snapshot: communityAuraSnapshotSchema.nullable(),
  generatedAt: dateTimeSchema,
}).strict();

/** One eligibility rule; `detail` defines the rule's terms (e.g. what an established X account is). */
export const communityBadgeCriterionSchema = z.object({
  label: z.string().min(1).max(160),
  detail: z.string().min(1).max(400).nullable(),
}).strict();

/**
 * The catalogue with live rarity and each badge's eligibility rules, for the "How Aura works" page,
 * the badge shelf and its detail dialog. Rules come from the badge definitions and the service's
 * live settings, so they always match what the evaluator checks.
 */
export const communityAuraBadgesResponseSchema = z.object({
  badges: z.array(z.object({
    id: achievementIdSchema,
    /**
     * The badge's lane, which picks its profile tab (Clout, Code, Capital). Holder badges are
     * passive (`holder`): no lane card or tab, only the All tab under its Holder heading.
     */
    lane: z.enum(["clout", "code", "capital", "holder"]),
    title: z.string().min(1).max(64),
    description: z.string().min(1).max(240),
    instruction: z.string().min(1).max(160),
    criteria: z.array(communityBadgeCriterionSchema).min(1).max(8),
    /** A profile trophy that adds no Aura (Firsts). */
    trophy: z.boolean(),
    rarity: communityBadgeRaritySchema.nullable(),
    /** Holder badges only: the collection's activation in the latest snapshot; `null` otherwise or before it. */
    activation: communityActivationSchema.nullable(),
    /** Holder badges only: when the collection's holdings were snapshotted; `null` for other badges. */
    snapshotAt: dateTimeSchema.nullable(),
  }).strict()),
  snapshot: communityAuraSnapshotSchema.nullable(),
}).strict();

/**
 * Published holder collections from the latest Aura snapshot: the podium (1, 2, 3) first, then the
 * rest by level, then activation share (`active / holders`), ties by more active holders, then
 * earlier reached count, then slug. Empty before the first snapshot.
 */
export const communityCollectionsResponseSchema = z.object({
  collections: z.array(z.object({
    id: achievementIdSchema,
    slug: z.string().regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/),
    title: z.string().min(1).max(120),
    holders: countSchema,
    active: countSchema,
    /** `active / holders`, 0 with no holders. */
    share: z.number().min(0).max(1),
    level: communityLevelSchema,
    nextLevel: communityNextLevelSchema.nullable(),
    podiumPlace: communityPodiumPlaceSchema.nullable(),
    bonus: countSchema,
  }).strict()),
  snapshot: communityAuraSnapshotSchema.nullable(),
}).strict();

/** Campaign counters for the Genesis header, all from the latest snapshot. */
export const communityGenesisStatsResponseSchema = z.object({
  members: countSchema,
  badgesEarned: countSchema,
  projectsVerified: countSchema,
  totalAura: countSchema,
  snapshot: communityAuraSnapshotSchema.nullable(),
}).strict();

/** The account-settings switch for showing the member's X name and picture on Genesis. */
export const communityDisplaySettingsRequestSchema = z.object({ xDisplay: z.boolean() }).strict();

export const communityDisplaySettingsResponseSchema = z.object({
  xDisplay: z.boolean(),
  alias: communityAliasSchema,
  /** Exactly what visitors currently see. */
  identity: communityPublicIdentitySchema,
}).strict();

export const communityAllowlistReasonCodeSchema = z.enum([
  "listed",
  "no_x",
  "disqualified",
  "no_wallet",
  "x_account_too_new",
  "not_ranked_high_enough",
  "criteria_not_met",
]);

/**
 * The member's whitelist status. `pending` until an operator publishes a frozen round; afterwards
 * `listed` names the round, wallet, tier and buying limit, `not_listed` gives one plain reason.
 */
export const communityAllowlistStatusResponseSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("pending"),
    /** The wallet that will be listed if the member qualifies (the chosen mint wallet). */
    wallet: addressSchema.nullable(),
    /** Live direct-inclusion eligibility, not a published place or proof. */
    tier: z.literal("gold").nullable(),
  }).strict(),
  z.object({
    status: z.literal("listed"),
    roundId: z.guid(),
    roundName: z.string().min(1).max(128),
    chainId: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
    collection: addressSchema,
    wallet: addressSchema,
    /** `null` for rounds without tiers (v1 criteria). */
    tier: genesisTierSchema.nullable(),
    entryLimit: z.number().int().min(1).max(10).nullable(),
  }).strict(),
  z.object({
    status: z.literal("not_listed"),
    roundId: z.guid(),
    roundName: z.string().min(1).max(128),
    reason: communityAllowlistReasonCodeSchema,
  }).strict(),
]);

/** Someone on the Genesis activity feed: their profile code and how they appear publicly. */
export const genesisActivityMemberSchema = z.object({
  code: referralCodeSchema,
  identity: communityPublicIdentitySchema,
}).strict();

export const genesisRankMilestoneSchema = z.enum(["first", "top3", "top10", "top100"]);

const activityIdSchema = z.string().regex(/^[1-9][0-9]{0,18}$/);

const activityBase = {
  /** Opaque, decreasing with age; pass as `before` for the next page. */
  id: activityIdSchema,
  at: dateTimeSchema,
  member: genesisActivityMemberSchema,
};

/**
 * Genesis-only activity, never part of the web app's public event stream. Each event is recorded
 * once, from a committed fact; names resolve when the feed is read, so a member who later hides
 * their X appears as their alias in past events. No wallets, person ids or project details.
 */
export const genesisActivityEventSchema = z.discriminatedUnion("kind", [
  /** Wallet verified and X linked. */
  z.object({ ...activityBase, kind: z.literal("member_joined") }).strict(),
  /** `member` invited `invitee`, who completed signup. */
  z.object({ ...activityBase, kind: z.literal("invite_completed"), invitee: genesisActivityMemberSchema }).strict(),
  /** A CODE project was verified. */
  z.object({ ...activityBase, kind: z.literal("build_verified") }).strict(),
  z.object({
    ...activityBase,
    kind: z.literal("badge_earned"),
    achievement: z.object({ id: achievementIdSchema, title: z.string().min(1).max(64) }).strict(),
  }).strict(),
  /** First time the member's Aura position reached this tier. */
  z.object({ ...activityBase, kind: z.literal("tier_reached"), tier: genesisTierSchema }).strict(),
  /** First time the member reached this leaderboard milestone. */
  z.object({ ...activityBase, kind: z.literal("rank_milestone"), milestone: genesisRankMilestoneSchema }).strict(),
]);

export const GENESIS_ACTIVITY_PAGE_SIZE = 50;

export const genesisActivityQuerySchema = z.object({ before: activityIdSchema.optional() }).strict();

export const genesisActivityResponseSchema = z.object({
  events: z.array(genesisActivityEventSchema).max(GENESIS_ACTIVITY_PAGE_SIZE),
  /** Pass as `before` for older events; `null` when there are none. */
  nextBefore: activityIdSchema.nullable(),
}).strict();

export type CommunityPublicIdentity = z.infer<typeof communityPublicIdentitySchema>;
export type CommunityReferralLookupResponse = z.infer<typeof communityReferralLookupResponseSchema>;
export type CommunityAuraStanding = z.infer<typeof communityAuraStandingSchema>;
export type CommunityBadgeRarity = z.infer<typeof communityBadgeRaritySchema>;
export type CommunityAuraSnapshot = z.infer<typeof communityAuraSnapshotSchema>;
export type CommunityAuraLeaderboardResponse = z.infer<typeof communityAuraLeaderboardResponseSchema>;
export type CommunityAuraBadgesResponse = z.infer<typeof communityAuraBadgesResponseSchema>;
export type CommunityGenesisStatsResponse = z.infer<typeof communityGenesisStatsResponseSchema>;
export type CommunityCollectionsResponse = z.infer<typeof communityCollectionsResponseSchema>;
export type CommunityDisplaySettingsRequest = z.infer<typeof communityDisplaySettingsRequestSchema>;
export type CommunityDisplaySettingsResponse = z.infer<typeof communityDisplaySettingsResponseSchema>;
export type CommunityAllowlistStatusResponse = z.infer<typeof communityAllowlistStatusResponseSchema>;
export type GenesisActivityMember = z.infer<typeof genesisActivityMemberSchema>;
export type GenesisActivityEvent = z.infer<typeof genesisActivityEventSchema>;
export type GenesisActivityResponse = z.infer<typeof genesisActivityResponseSchema>;
export type GenesisRankMilestone = z.infer<typeof genesisRankMilestoneSchema>;
